// ─────────────────────────────────────────────────────────────
// poPrint.config.js — Report 1: single Purchase Order print
// Shape: document (landscape)
// Data:  GET /api/tape/purchase-orders/:id  (header + items[])
// ─────────────────────────────────────────────────────────────

import { TERMS_PO, SIGNATURES, FORMATTERS } from '../../print/printConfig';

const num = (v) => (isNaN(parseFloat(v)) ? 0 : parseFloat(v));

export const poPrintConfig = (po) => {
  const items = po.items || [];

  const gstAmount = num(po.gst_amount);
  const total = num(po.total_amount);
  const subtotal = total - gstAmount;

  const gstLabel =
    po.gst_mode === 'percent' && num(po.gst_value) > 0
      ? `GST (${num(po.gst_value)}%)`
      : 'GST';

    const rows = items.map((it) => ({
    jumbo: it.tape_type_name,
           // What the vendor is being asked to supply, in his own terms:
    // thickness, width, length. The core spec never appears — that is
    // our cutting plan, decided after the roll arrives.
    spec: [
      it.micron ? `${FORMATTERS.qty(it.micron)} micron` : null,
      it.width_mm ? `${FORMATTERS.int(it.width_mm)} mm` : null,
      it.length_per_roll_m ? `${FORMATTERS.int(it.length_per_roll_m)} m / roll` : null,
    ].filter(Boolean).join('  ·  '),
    uom: 'Roll',
    quantity: it.quantity,
    weight_per_roll_kg: it.weight_per_roll_kg,
    rate_per_kg: it.rate_per_kg,
    rate: it.rate,
    amount: it.amount,
  }));

    // GST appears only when there is some. A PO to an unregistered
  // vendor carries no tax, and a row reading "GST 0" invites him to
  // ask why it is on the document at all.
    // Subtotal and GST travel together. Without GST the subtotal is the
  // grand total written twice; with it, the subtotal is the figure the
  // tax was worked out on, and a GST line with no base to check it
  // against is what an accounts department queries.
  const totals = [
    ...(gstAmount > 0
      ? [
          { label: 'Goods Subtotal', value: subtotal, fmt: 'money' },
          { label: gstLabel, value: gstAmount, fmt: 'money' },
        ]
      : []),
    { label: 'Grand Total', value: total, fmt: 'money', emphasis: true, tone: 'net' },
  ];

  const totalRolls = items.reduce((s, it) => s + num(it.quantity), 0);
  const totalKg = items.reduce(
    (s, it) => s + num(it.quantity) * num(it.weight_per_roll_kg),
    0
  );

  return {
    kind: 'document',
    orientation: 'l',
    title: 'Purchase Order',

    boxes: [
      {
        heading: 'Vendor Details',
        rows: [
          { label: 'Vendor Name', value: po.vendor_name },
          { label: 'Address', value: po.vendor_address },
          { label: 'Contact Person', value: po.vendor_contact_person },
                    { label: 'Phone', value: po.vendor_phone },
        ],
      },
      {
        heading: 'Purchase Order Details',
        rows: [
          {
            label: 'PO Date',
            value: FORMATTERS.date(po.po_date),
            label2: 'PO #',
            value2: po.po_number,
          },
                    {
            label: 'Total Rolls',
            value: FORMATTERS.qty(totalRolls),
            // Term 1 says credit days start on receipt of the invoice.
            // With no figure printed anywhere the clause referred to
            // nothing the vendor could read.
            label2: 'Credit',
            value2: Number(po.credit_days) > 0
              ? `${Number(po.credit_days)} days`
              : 'Cash',
          },
        ],
      },
    ],

    columns: [
            { key: 'jumbo', label: 'Tape', align: 'left', fmt: 'text', width: 40 },
      { key: 'spec', label: 'Specification', align: 'left', fmt: 'text' },
            { key: 'uom', label: 'Unit', align: 'center', fmt: 'text', width: 14 },
      { key: 'quantity', label: 'Qty', align: 'right', fmt: 'qty', width: 16 },
      { key: 'weight_per_roll_kg', label: 'Wt / Roll', align: 'right', fmt: 'kg', width: 22 },
      { key: 'rate_per_kg', label: 'Rate / kg', align: 'right', fmt: 'rate', width: 22 },
      { key: 'rate', label: 'Rate / Roll', align: 'right', fmt: 'money', width: 24 },
      { key: 'amount', label: 'Amount', align: 'right', fmt: 'money', width: 28, bold: true },
    ],

    rows,

    footRow: [
      'TOTAL',
      '',
      '',
      FORMATTERS.qty(totalRolls),
      FORMATTERS.kg(totalKg),
      '',
      '',
      FORMATTERS.money(subtotal),
    ],

        totals,

    terms: TERMS_PO,
    signatures: SIGNATURES.po,
  };
};

export default poPrintConfig;